/* eslint-disable camelcase */
import qs from 'qs';
import { sendEvent, sendTechEvent } from 'services/analytics';
import { updateUserLoginStatus, userLogout } from 'actions/user';
import { loadUser } from 'actions/user/loadUser';
import { init as initPaymentStore } from 'actions/inAppPayment';
import { featureUsed } from 'actions/featureUsage';
import { openModal } from 'actions/modals';
import history from 'services/history';
import { isIosApp } from 'services/environment';
import { updateCustomAttributes } from 'services/intercom';

import {
  AUTH_IN_PROGRESS,
  AUTH_USER,
  AUTH_ERROR,
  SIGNUP_BY_EMAIL_IN_PROGRESS,
  SIGNUP_BY_EMAIL,
  SIGNUP_ERROR,
  NATIVE_CALLBACK_URL,
  SOCIAL_AUTH_IN_PROGRESS,
  SOCIAL_AUTH_COMPLETE,
  SOCIAL_AUTH_CANCEL,
} from '../constants';
import { MODALS } from '../constants/view';

const convertError = response => {
  console.warn(
    'Original auth error:',
    response && response.error ? JSON.stringify(response.error) : response,
  );
  let error;
  try {
    error = JSON.parse(response.error);
  } catch (ignore) {
    // Ignore
  }
  if (error) {
    return new Error(
      error.friendly_message || error.description || error.error_description || 'Unspecified Error',
    );
  }
  return new Error('Login failed - please try again later');
};

const decodeAudience = apiAudience => {
  try {
    return atob(apiAudience);
  } catch (err) {
    return apiAudience;
  }
};

const exchangeForToken = async (domain, params) => {
  const tokenEndpoint = `https://${domain}/oauth/token`;
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };
  if (window.cordova && window.cordova.plugin && window.cordova.plugin.http) {
    // In cordova we need to use the native http plugin to avoid CORS.
    // Auth0 doesn't allow an origin of 'null'
    const options = {
      method: 'post',
      data: params,
      headers,
      responseType: 'json',
      timeout: 5,
    };
    const response = await new Promise((resolve, reject) => {
      window.cordova.plugin.http.sendRequest(tokenEndpoint, options, resolve, err => {
        console.warn('Failed to exchange for token', JSON.stringify({ params, err }, null, 2));
        reject(convertError(err));
      });
    });
    if (response.status === 200) {
      return response.data;
    }
    throw new Error('Non-success response from token endpoint', response);
  } else {
    throw new Error('Native login only works from within Cordova');
  }
};

const exchangeForUser = async (apiUrl, params) => {
  // Let the server know we logged in. We can either send it the authorization code and let it fetch the tokens (authorization-code flow),
  // or send it the tokens for the embedded password based login (Resource Owner password flow).
  const body = qs.stringify({ ...params, native: true });

  const response = await fetch(`${apiUrl}/login/auth0/return`, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
    },
    body,
  });
  if (response.status === 200) {
    return response.json();
  }
  const responseText = await response.text();
  let responseObj;
  try {
    responseObj = JSON.parse(responseText);
  } catch (err) {
    console.warn(`Response from ${apiUrl} posted`, responseText);
    throw new Error('Access denied');
  }
  throw new Error(responseObj.message || 'Access denied');
};

const signup = async (domain, params) => {
  const signupEndpoint = `https://${domain}/dbconnections/signup`;
  const headers = {
    'Content-Type': 'application/json',
  };
  if (window.cordova && window.cordova.plugin && window.cordova.plugin.http) {
    // In cordova we need to use the native http plugin to avoid CORS.
    // Auth0 doesn't allow an origin of 'null'
    const options = {
      method: 'post',
      data: params,
      headers,
      serializer: 'json',
      responseType: 'json',
      timeout: 5,
    };
    const response = await new Promise((resolve, reject) => {
      window.cordova.plugin.http.sendRequest(signupEndpoint, options, resolve, err =>
        reject(convertError(err)),
      );
    });
    if (response.status === 200) {
      return response.data;
    }
    throw convertError(response);
  } else {
    throw new Error('Native signup only works from within Cordova');
  }
};

const doShowPremiumPromo = (dispatch, getState, onPromoClose) => {
  setTimeout(() => {
    const { ready } = getState().inAppPayment;
    if (ready) {
      import('components/InAppPaymentInfo/InAppPaymentInfo.jsx' /* webpackChunkName: "in-app-payment", webpackMode: "lazy" */).then(
        module => {
          dispatch(
            openModal({
              name: MODALS.IN_APP_PAYMENT,
              content: module.default,
              props: { withSkipButton: true, onPromoClose },
            }),
          );
        },
      );
    } else {
      doShowPremiumPromo(dispatch, getState, onPromoClose);
    }
  }, 1000);
};

const showPremiumPromo = (dispatch, getState) =>
  new Promise(resolve => doShowPremiumPromo(dispatch, getState, resolve));

const continueJourney = returnTo => {
  if (returnTo) {
    if (typeof returnTo === 'string') {
      try {
        history.replace(JSON.parse(returnTo));
        return;
      } catch (err) {
        // Ignore
      }
    }
    history.replace(returnTo);
    return;
  }
  history.replace('/');
};

async function storeRefreshToken(token) {
  // Save in the keychain if available.
  // Localstorage on iOS can disappear whenever storage is low.
  let saved = false;
  try {
    if (window.Keychain) {
      await new Promise((resolve, reject) =>
        window.Keychain.set(resolve, reject, 'refresh_token', token, false),
      );
      saved = true;
    }
  } catch (err) {
    if (isIosApp()) {
      console.warn('Failed to save the refresh token to the keychain');
      sendTechEvent('auth.tokenRefreshFailure', {
        code: 'KEYCHAIN_SAVE_FAIL',
        error: err.message,
      });
    }
  }
  if (!saved) {
    localStorage.setItem('refresh_token', token);
  }
}

async function getRefreshToken() {
  let refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken && window.Keychain) {
    try {
      refreshToken = await new Promise((resolve, reject) =>
        window.Keychain.get(resolve, reject, 'refresh_token'),
      );
    } catch (err) {
      console.warn('Failed to get the refresh token from the keychain');
      if (isIosApp()) {
        sendTechEvent('auth.tokenRefreshFailure', {
          code: 'KEYCHAIN_LOOKUP_FAIL',
          error: err.message,
        });
      }
    }
  }
  return refreshToken;
}

async function clearRefreshToken() {
  if (window.Keychain) {
    try {
      await new Promise((resolve, reject) =>
        window.Keychain.remove(resolve, reject, 'refresh_token'),
      );
    } catch (err) {
      // Ignore
    }
  }
  localStorage.removeItem('refresh_token');
}

export function logout() {
  return async (dispatch, getState, { client }) => {
    sendTechEvent('auth.logout');
    dispatch(userLogout());

    // Clear the browser cache (iOS only right now)
    if (window.WkCacheClear) {
      try {
        window.WkCacheClear(
          { domain: 'ckbk.com', delete: ['cookies', 'assets'] },
          msg => {
            console.info('Cleared the browser cache', msg);
          },
          error => {
            console.warn('Could not clear browser cache', error);
          },
        );
      } catch (err) {
        console.error('Could not clear browser cache', err);
      }
    }

    // Hide the status bar
    if (window.StatusBar) {
      window.StatusBar.hide();
    }

    // Clear the Apollo cache
    await client.clearStore();

    // Clear local storage
    try {
      localStorage.clear();
      localStorage.setItem('loggedOut', 'true');
    } catch (err) {
      console.warn('Failed to clear local storage', err);
    }

    // Clear the refresh token from the keychain
    try {
      await clearRefreshToken();
    } catch (err) {
      console.warn('Failed to clear the refresh token', err);
    }

    // Reset Google Analytics
    if (window.cordova.plugins?.firebase?.analytics) {
      try {
        await window.cordova.plugins.firebase.analytics.resetAnalyticsData();
      } catch (err) {
        console.warn('Failed to reset analytics data', err);
      }
    }

    sendEvent({
      event: 'logout',
      method: 'native',
    });
    history.push('/');
  };
}

export function login({ email, password, returnTo }) {
  return async (dispatch, getState) => {
    const { apiUrl } = getState().config;
    const { domain, apiAudience, scope, clientId, realm } = getState().config.auth;
    let success = false;

    dispatch({ type: AUTH_IN_PROGRESS });
    try {
      const credentials = await exchangeForToken(domain, {
        grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
        username: email,
        password,
        audience: decodeAudience(apiAudience),
        scope,
        client_id: clientId,
        realm,
      });
      const authResponse = await exchangeForUser(apiUrl, credentials);
      console.info('AuthResponse', authResponse);
      await storeRefreshToken(authResponse.credentials.refresh_token);
      await dispatch(updateUserLoginStatus(authResponse));
      await dispatch(loadUser());
      dispatch(initPaymentStore());
      dispatch({ type: AUTH_USER });
      dispatch(featureUsed('feature_ios_app'));
      sendEvent({
        event: 'login',
        method: 'native',
      });
      success = true;
      continueJourney(returnTo, dispatch);
      if (!getState().user.subscription?.is_valid) {
        await showPremiumPromo(dispatch, getState);
        updateCustomAttributes({ registration_complete: true });
      }
      sendTechEvent('auth.loginSuccess');
    } catch (error) {
      console.error('Failed to login', error);
      await clearRefreshToken();
      sendEvent({
        event: 'login_failed',
        method: 'native',
      });
      sendTechEvent('auth.loginFail', { error: error.message });
      dispatch({
        type: AUTH_ERROR,
        error: error.message,
      });
      dispatch(updateUserLoginStatus({ logged_in: false, credentials: null }));
      return false;
    }
    try {
      localStorage.setItem('user', JSON.stringify({ ...getState().user, collections: {} }));
    } catch (error) {
      console.error('Failed to store user', error);
    }
    return success;
  };
}

export function register({ email, password, metadata, returnTo }) {
  return async (dispatch, getState) => {
    const { domain, clientId, realm } = getState().config.auth;

    dispatch({ type: SIGNUP_BY_EMAIL_IN_PROGRESS });
    try {
      await signup(domain, {
        client_id: clientId,
        email,
        password,
        connection: realm,
        given_name: metadata.given_name,
        family_name: metadata.family_name,
        name: metadata.name,
        user_metadata: metadata,
      });
      await dispatch(login({ email, password }));
      dispatch({ type: SIGNUP_BY_EMAIL });
      continueJourney(returnTo, dispatch);
      if (!getState().user.subscription?.is_valid) {
        await showPremiumPromo(dispatch, getState);
        updateCustomAttributes({ registration_complete: true });
      }
      sendTechEvent('auth.signupSuccess');
      return true;
    } catch (error) {
      dispatch({
        type: SIGNUP_ERROR,
        error: error.message,
        code: error.code || error.name,
      });
      sendTechEvent('auth.signupFail', { error: error.message });
      throw error;
    }
  };
}

export function refreshAccessToken() {
  return async (dispatch, getState) => {
    const state = getState();
    const { domain, clientId } = state.config.auth;
    const { apiUrl } = state.config;
    const { user } = state;

    const refreshToken = await getRefreshToken();

    if (!refreshToken) {
      if (user && user.logged_in) {
        console.error('No Refresh token found');
        sendTechEvent('auth.tokenRefreshFailure', {
          code: 'NO_REFRESH_TOKEN',
          error: 'Token not found',
        });
        dispatch(logout());
      }
      return;
    }

    try {
      const credentials = await exchangeForToken(domain, {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: clientId,
      });
      console.info('Used refresh_token ok');
      const authResponse = await exchangeForUser(apiUrl, credentials);
      // The auth server will not send us a refresh token unless we have enabled rotation.
      if (authResponse.credentials.refresh_token) {
        await storeRefreshToken(authResponse.credentials.refresh_token);
      }
      await dispatch(updateUserLoginStatus(authResponse));
      sendTechEvent('auth.tokenRefreshSuccess');
    } catch (error) {
      console.error('Failed to refresh login', error);
      sendTechEvent('auth.tokenRefreshFailure', { code: 'REFRESH_ERROR', error: error.message });
      sendEvent({
        event: 'login_failed',
        method: 'native',
      });
    }
    try {
      localStorage.setItem('user', JSON.stringify(getState().user));
    } catch (error) {
      console.error('Failed to store user', error);
    }
  };
}

function completeSocialLogin(authResponse, returnTo) {
  return async (dispatch, getState) => {
    await storeRefreshToken(authResponse.credentials.refresh_token);
    await dispatch(updateUserLoginStatus(authResponse));
    await dispatch(loadUser());
    dispatch(initPaymentStore());
    sendEvent({
      event: 'login',
      method: 'native',
    });
    dispatch({ type: SOCIAL_AUTH_COMPLETE });
    dispatch(featureUsed('feature_ios_app'));
    sendTechEvent('auth.socialLoginSuccess');
    continueJourney(returnTo, dispatch);
    if (!getState().user.subscription?.is_valid) {
      await showPremiumPromo(dispatch, getState);
      updateCustomAttributes({ registration_complete: true });
    }
  };
}

export function handleAuthCallback(url) {
  return async (dispatch, getState) => {
    try {
      const callbackUrl = new URL(url);
      const code = callbackUrl.searchParams.get('code');
      const error = callbackUrl.searchParams.get('error');
      const errorDescription = callbackUrl.searchParams.get('error_description');
      const returnTo = callbackUrl.searchParams.get('state');

      if (error) {
        sendEvent({
          event: 'login_failed',
          method: 'native',
          error,
        });
        dispatch({
          type: AUTH_ERROR,
          error: errorDescription,
        });
      } else if (code) {
        const { apiUrl } = getState().config;
        const authResponse = await exchangeForUser(apiUrl, { code, native: !!window.cordova });
        dispatch(completeSocialLogin(authResponse, returnTo));
      } else {
        console.warn('Unexpected response from auth server', callbackUrl);
      }
    } catch (error) {
      // Usually caused by the user pressing cancel, but not sure how to detect.
      console.warn(`Error during social login callback for ${url}`, error);
    }
    try {
      localStorage.setItem('user', JSON.stringify({ ...getState().user, collections: {} }));
    } catch (error) {
      console.error('Failed to store user', error);
    }
  };
}

export function signinWithApple() {
  return async (dispatch, getState) => {
    if (!(window.cordova && window.cordova.plugins && window.cordova.plugins.SignInWithApple)) {
      console.error('SigninWithApple plugin not available');
      return;
    }
    const { apiUrl } = getState().config;
    const { domain, apiAudience, scope, clientId } = getState().config.auth;
    try {
      const appleCredentials = await new Promise((resolve, reject) => {
        window.cordova.plugins.SignInWithApple.signin({ requestedScopes: [0, 1] }, resolve, reject);
      });
      const auth0Credentials = await exchangeForToken(domain, {
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        subject_token: appleCredentials.authorizationCode,
        audience: decodeAudience(apiAudience),
        scope,
        client_id: clientId,
        subject_token_type: 'http://auth0.com/oauth/token-type/apple-authz-code',
      });
      const authResponse = await exchangeForUser(apiUrl, auth0Credentials);
      await dispatch(completeSocialLogin(authResponse));
    } catch (err) {
      if (err.code === '1001') {
        dispatch({ type: SOCIAL_AUTH_CANCEL });
      } else {
        dispatch({ type: AUTH_ERROR, error: err.localizedDescription || err.message });
        sendTechEvent('auth.socialLoginFail', {
          provider: 'apple',
          error: err.localizedDescription || err.message,
        });
      }
      console.error('Failed to sign in with Apple', err);
    }
  };
}

export function socialLogin(provider, returnTo) {
  return async (dispatch, getState) => {
    const state = getState();
    const { domain, apiAudience, scope, clientId } = state.config.auth;
    const { device } = state;
    dispatch({ type: SOCIAL_AUTH_IN_PROGRESS, payload: { provider } });
    if (
      provider === 'apple' &&
      device.platform &&
      device.platform === 'iOS' &&
      device.majorVersion >= 13
    ) {
      await dispatch(signinWithApple());
    } else {
      const url = new URL(`https://${domain}/authorize`);
      url.searchParams.append('client_id', clientId);
      url.searchParams.append('response_type', 'code');
      url.searchParams.append('connection', provider);
      url.searchParams.append('redirect_uri', NATIVE_CALLBACK_URL);
      url.searchParams.append('scope', scope);
      url.searchParams.append('audience', decodeAudience(apiAudience));
      if (returnTo) {
        url.searchParams.append('state', JSON.stringify(returnTo));
      }

      try {
        if (device && device.available && device.platform === 'iOS' && device.majorVersion >= 12) {
          // iOS 12 and above we can use an embedded window
          const callbackUrl = await new Promise((resolve, reject) => {
            window.plugins.ASWebAuthSession.start(
              encodeURIComponent(NATIVE_CALLBACK_URL),
              url.href,
              resolve,
              reject,
            );
          });
          await dispatch(handleAuthCallback(callbackUrl));
        } else {
          window.open(url.href, '_system');
        }
      } catch (error) {
        // Usually caused by the user pressing cancel, but not sure how to detect.
        console.warn('Error during social login init', error);
        dispatch({ type: SOCIAL_AUTH_CANCEL });
      }
    }
  };
}

export default {
  login,
  logout,
  socialLogin,
  refreshAccessToken,
  register,
};
